<template>
  <div>
    <Header>
      Dodawanie grafiku
    </Header>

    <b-row>
      <b-col xl="6">
        <Errors/>

        <b-form @submit.prevent="submit">
          <b-form-group label="Fryzjer:">
            <b-form-select
              v-model="form.barberIds"
              :options="select_barbers"
              multiple
              required
            />
          </b-form-group>
          <b-form-group>
            <b-checkbox v-model="form.isOnline">
              Rezerwacje online
            </b-checkbox>
          </b-form-group>
          <b-form-group label="Dni:">
            <b-check-group
              v-model="form.dateStrings"
              stacked
            >
              <div v-for="(date, idx) in dates" :key="date.date_string">
                <hr v-if="date.day_of_week === 1 && idx !== 0">
                <b-form-checkbox
                  :value="date.date_string"
                  :disabled="shouldBeDisabled(date.date_string)"
                  class="my-2"
                >
                  <span :class="{'text-danger': date.is_holiday}">
                    {{ date.date_string }}
                    - {{ date.is_today ? '(Dziś)' : date.name }}
                    <b-icon v-if="date.is_holiday" icon="x-lg" variant="danger"/>
                    <span v-for="barber in selectedBarbers">
                      <span v-if="isSchedule(date.date_string, barber.id)">
                        <b-icon v-if="isSingleBarber" icon="check-lg" variant="success" />
                        <b-avatar v-else variant="success" :text="barber.initials" size="1em" class="mr-1" />
                      </span>
                    </span>
                  </span>
                </b-form-checkbox>
              </div>
            </b-check-group>
            <b-input v-model="endDate" type="date" :min="todayString"/>
          </b-form-group>
          <b-form-group label="Godziny:">
            <b-row>
              <b-col>
                <b-form-group label="Od:">
                  <b-form-input v-model="startTime" type="time" step="900" size="lg" :state="startTimeState" @focus.native="presetTime('start')"/>
                  <b-form-invalid-feedback :state="startTimeState">
                    Minuty muszą być co 15 (00, 15, 30, 45)
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group label="Do:">
                  <b-form-input v-model="endTime" type="time" step="900" size="lg" :state="endTimeState" @focus.native="presetTime('end')"/>
                  <b-form-invalid-feedback :state="endTimeState">
                    Minuty muszą być co 15 (00, 15, 30, 45)
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form-group>
          <b-form-group>
            <div class="d-flex justify-content-between">
              <div>
                <inertia-link
                  :href="route('schedules.index')"
                  as="b-button"
                  variant="secondary"
                >
                  <b-icon icon="x"/>
                  Anuluj
                </inertia-link>
              </div>
              <div>
                <b-button type="submit" variant="primary" :disabled="form.processing || hasInvalidTime">
                  <b-icon icon="plus"/>
                  Dodaj
                </b-button>
              </div>
            </div>
          </b-form-group>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Layout from '@/Layouts/Default'
import Header from '@/Layouts/Components/Header'
import Errors from '@/Layouts/Components/Errors'
import PolishHolidays from '@/Mixins/PolishHolidays'
import _ from 'lodash'

function pad2(value) {
  return String(value).padStart(2, '0')
}

function toDateString(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

// Default end of the schedule range: current week + 2 more (up to Sunday),
// and when today is Thursday/Friday/Saturday we add one more week.
// Sundays are never scheduled, so by default we show the last working day (Saturday).
function defaultEndDate(today) {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14)
  const dayOfWeek = end.getDay() // 0 = Sunday
  end.setDate(end.getDate() + (dayOfWeek === 0 ? 0 : 7 - dayOfWeek))
  if (today.getDay() >= 4) {
    end.setDate(end.getDate() + 7)
  }
  end.setDate(end.getDate() - 1)
  return end
}

export default {
  components: {
    Header,
    Errors,
  },
  mixins: [PolishHolidays],
  layout: Layout,
  props: {
    barbers: Array,
  },
  data() {
    const today = startOfDay(new Date())
    return {
      today,
      endDate: toDateString(defaultEndDate(today)),
      form: this.$inertia.form({
        barberIds: [],
        dateStrings: [],
        // Empty by default; the picker is preset on first open (see presetTime)
        startAt: '',
        endAt: '',
        isOnline: false,
      }),
      schedulesBetween: {},
    }
  },
  watch: {
    'form.barberIds': function () {
      setTimeout(() => {
        this.fetchSchedulesBetween()
      }, 50)
    },
    endDate: function () {
      // After the range changes, drop checked days that fell outside the list
      const available = this.dates.map((date) => date.date_string)
      this.form.dateStrings = this.form.dateStrings.filter((dateString) => available.includes(dateString))
      this.fetchSchedulesBetween()
    },
  },
  computed: {
    // Bridge the form's "HH:MM:SS" value to the native time input's "HH:MM"
    startTime: {
      get() {
        return this.form.startAt ? this.form.startAt.slice(0, 5) : ''
      },
      set(value) {
        this.form.startAt = value ? `${value}:00` : ''
      },
    },
    endTime: {
      get() {
        return this.form.endAt ? this.form.endAt.slice(0, 5) : ''
      },
      set(value) {
        this.form.endAt = value ? `${value}:00` : ''
      },
    },
    startTimeState() {
      return this.timeStepState(this.form.startAt)
    },
    endTimeState() {
      return this.timeStepState(this.form.endAt)
    },
    hasInvalidTime() {
      return this.startTimeState === false || this.endTimeState === false
    },
    todayString() {
      return toDateString(this.today)
    },
    dates() {
      const dates = []
      if (!this.endDate) {
        return dates
      }

      const [year, month, day] = this.endDate.split('-').map(Number)
      const end = new Date(year, month - 1, day)
      const cursor = new Date(this.today)

      while (cursor <= end) {
        if (cursor.getDay() !== 0) { // skip Sundays
          const dateString = toDateString(cursor)
          const isHoliday = this.isPolishHoliday(cursor)
          dates.push({
            date_string: dateString,
            // on a day off show the holiday name instead of the weekday name
            name: isHoliday ? this.polishHolidayName(cursor) : this.polishWeekdayName(cursor),
            is_today: dateString === this.todayString,
            is_holiday: isHoliday,
            day_of_week: cursor.getDay(),
          })
        }
        cursor.setDate(cursor.getDate() + 1)
      }

      return dates
    },
    select_barbers() {
      return this.barbers.map((barber) => {
        return {
          value: barber.id,
          text: barber.name,
        }
      })
    },
    selectedBarbers() {
      return this.barbers.filter((barber) => this.form.barberIds.includes(barber.id))
    },
    isSingleBarber() {
      return this.form.barberIds.length === 1
    },
  },
  mounted() {
    if (this.barbers.length === 1) {
      this.form.barberIds = [this.barbers[0].id]
    }
  },
  methods: {
    submit() {
      if (this.hasInvalidTime) return

      this.form
        .transform((data) => _.pickBy(data))
        .post(this.route('schedules.store'))
    },
    // false (invalid) when minutes are not a multiple of 15, otherwise null (no styling)
    timeStepState(value) {
      if (!value) {
        return null
      }
      return Number(value.slice(3, 5)) % 15 === 0 ? null : false
    },
    // On first open, start the time picker at working hours instead of the current time
    presetTime(which) {
      if (which === 'start' && !this.form.startAt) {
        this.form.startAt = '09:00:00'
      } else if (which === 'end' && !this.form.endAt) {
        this.form.endAt = '17:00:00'
      }
    },
    isSchedule(dateString, barberId) {
      return this.schedulesBetween[dateString]?.some((schedule) => schedule.barber_id === barberId)
    },
    shouldBeDisabled(dateString) {
      if (!this.schedulesBetween[dateString]) {
        return false
      }
      return this.schedulesBetween[dateString].length >= this.form.barberIds.length
    },
    async fetchSchedulesBetween() {
      this.schedulesBetween = {}

      if (this.form.barberIds.length <= 0 || this.dates.length === 0) {
        return
      }

      try {
        const {data} = await this.axios.get(this.route('schedules.getBetween'), {
          params: {
            barberIds: this.form.barberIds,
            startAt: _.head(this.dates).date_string,
            endAt: _.last(this.dates).date_string,
          }
        })

        this.schedulesBetween = _.groupBy(data, 'date_string')
      } catch (e) {
      }
    },
  },
}
</script>
